import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Music, Video, Loader2, Disc3, ExternalLink, RotateCcw, AlertTriangle, Info } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { StatusPill } from "@/components/dashboard/ui";

const SERVICE_LABELS = {}; // filled below

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "");
const fmtDT = (v) => (v ? new Date(v).toLocaleString() : "");
const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "");
const nn = (v) => (v === undefined || v === null || v === "" ? null : v);

/**
 * Shared release detail viewer for Artists, Labels and Super Admins.
 * Renders every stored field grouped by section (Album / Rights / Contributors / Restrictions),
 * plus artwork, per-track audio players, downloadable ZIP + revert-to-draft.
 */
export default function ReleaseDetailDialog({ open, onOpenChange, releaseId, isOwner = false, onChanged }) {
  const [rel, setRel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reverting, setReverting] = useState(false);

  const reload = async () => {
    if (!releaseId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/releases/${releaseId}`);
      setRel(data);
    } catch (e) {
      toast.error("Couldn't load release");
      onOpenChange(false);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || !releaseId) { setRel(null); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, releaseId]);

  const downloadZip = async () => {
    if (!releaseId) return;
    setDownloading(true);
    try {
      const res = await api.get(`/releases/${releaseId}/download`, { responseType: "blob" });
      const blob = res.data;
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m?.[1] || `${(rel?.title || "release").replace(/[^a-z0-9-_ ]/gi, "")}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err) {
      toast.error("Download failed");
    } finally { setDownloading(false); }
  };

  const revert = async () => {
    if (!releaseId) return;
    if (!confirm("Pull this release back to Draft so you can edit it? Live stores will keep serving the current version until you resubmit.")) return;
    setReverting(true);
    try {
      const { data } = await api.post(`/artist/releases/${releaseId}/revert`);
      setRel({ ...data, tracks: rel?.tracks || [] });
      toast.success("Back to draft — edit and resubmit when ready.");
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't revert this release"));
    } finally { setReverting(false); }
  };

  const isVideo = rel?.type === "Music Video";
  const status = rel?.status;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0b1220] border-white/10 text-white sm:max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="release-detail-dialog"
      >
        {loading || !rel ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
        ) : (
          <>
            {/* Sticky header */}
            <div className="flex items-start gap-4 px-6 pt-6 pb-4 border-b border-white/5 pr-14">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">
                  {isVideo ? <Video size={11} className="text-fuchsia-400" /> : <Music size={11} className="text-blue-400" />}
                  {rel.type || "Release"}
                  {status && <StatusPill status={status} />}
                </div>
                <h2 className="mt-2 font-display text-2xl md:text-3xl tracking-tight truncate">
                  {rel.title}{rel.version ? <span className="text-slate-500 font-normal"> ({rel.version})</span> : null}
                </h2>
                <div className="mt-1 text-sm text-slate-400 truncate">
                  {rel.display_artist || rel.artist_name}{rel.label ? ` · ${rel.label}` : ""}
                </div>
              </div>
            </div>

            {/* Scroll area */}
            <div className="overflow-y-auto px-6 py-5 space-y-6">
              {status === "Rejected" && (rel.rejection_reason || "").trim() && (
                <div data-testid="rejected-banner" className="rounded-2xl border border-red-500/30 bg-red-500/[0.06] p-4 flex items-start gap-3">
                  <AlertTriangle size={18} className="text-red-300 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-red-300">Rejected</div>
                    <p className="mt-1 text-sm text-red-100 whitespace-pre-wrap">{rel.rejection_reason}</p>
                    <div className="mt-1 text-[11px] text-red-200/70">{fmtDT(rel.rejected_at)}</div>
                  </div>
                </div>
              )}

              {status === "Draft" && (rel.revision_notes || "").trim() && (
                <div data-testid="revision-banner" className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4 flex items-start gap-3">
                  <Info size={18} className="text-amber-300 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-amber-300">Sent back for changes</div>
                    <p className="mt-1 text-sm text-amber-100 whitespace-pre-wrap">{rel.revision_notes}</p>
                    <div className="mt-1 text-[11px] text-amber-200/70">{fmtDT(rel.sent_back_at)}</div>
                  </div>
                </div>
              )}

              {status === "Draft" && rel.was_live_before_edit && !(rel.revision_notes || "").trim() && (
                <div className="rounded-2xl border border-slate-500/25 bg-slate-500/[0.05] p-4 text-sm text-slate-300 flex items-start gap-3">
                  <Info size={18} className="text-slate-300 mt-0.5 shrink-0" />
                  <div>Draft reverted from a Live release — stores will keep the current version until you resubmit and it's approved again.</div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Artwork */}
                <div className="md:col-span-1">
                  {rel.cover_url ? (
                    <img
                      data-testid="release-detail-artwork"
                      src={mediaUrl(rel.cover_url)}
                      alt="Cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling?.classList.remove("hidden"); }}
                      className={`w-full ${isVideo ? "aspect-video" : "aspect-square"} rounded-2xl object-cover border border-white/10 shadow-2xl`}
                    />
                  ) : null}
                  <div className={`w-full ${isVideo ? "aspect-video" : "aspect-square"} rounded-2xl border border-dashed border-white/10 bg-white/[0.02] flex items-center justify-center ${rel.cover_url ? "hidden" : ""}`}>
                    <Disc3 size={44} className="text-slate-600" />
                  </div>
                  <div className="mt-3 flex flex-col gap-2">
                    <button
                      type="button"
                      data-testid="release-detail-download-zip"
                      disabled={downloading}
                      onClick={downloadZip}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-medium px-5 py-2.5 hover:bg-slate-200 active:scale-[0.98] transition-all disabled:opacity-60"
                    >
                      {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download bundle (.zip)
                    </button>
                    {isOwner && status && status !== "Draft" && (
                      <button
                        type="button"
                        data-testid="release-detail-revert"
                        disabled={reverting}
                        onClick={revert}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 text-white px-5 py-2.5 hover:bg-white/5 transition-colors disabled:opacity-60"
                      >
                        {reverting ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Revert to draft &amp; edit
                      </button>
                    )}
                    {rel.pdf_booklet_url && (
                      <a
                        data-testid="release-detail-booklet"
                        href={mediaUrl(rel.pdf_booklet_url)}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 text-white px-5 py-2.5 hover:bg-white/5 transition-colors"
                      >
                        <FileText size={15} /> Open booklet <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>

                {/* Metadata: full grouped sections */}
                <div className="md:col-span-2 space-y-6">
                  <Section title="Album">
                    <Grid rows={[
                      ["Title", nn(rel.title)],
                      ["Version", nn(rel.version)],
                      ["Display artist", nn(rel.display_artist)],
                      ["Featured artists", nn(rel.featured_artists)],
                      ["UPC / EAN", nn(rel.upc) || "UPC pending"],
                      ["Catalog #", nn(rel.catalog_number)],
                      ["Genre", nn(rel.genre)],
                      ["Subgenre", nn(rel.subgenre)],
                      ["Primary language", nn(rel.primary_language)],
                      ["Metadata language", nn(rel.metadata_language)],
                      ["Audio resolution", nn(rel.audio_resolution)],
                      ["Compilation", rel.compilation ? "Yes" : null],
                    ]} />
                  </Section>

                  <Section title="Dates & release plan">
                    <Grid rows={[
                      ["Release date", fmtDate(rel.release_date) || null],
                      ["Original release", fmtDate(rel.original_release_date) || null],
                      ["Pre-order date", fmtDate(rel.pre_order_date) || null],
                      ["Timed release", nn(rel.timed_release)],
                      ["Exclusive", nn(rel.exclusive)],
                      ["Created", fmtDT(rel.created_at)],
                      ["Submitted", fmtDT(rel.submitted_at) || null],
                      ["Made live", fmtDT(rel.live_at) || null],
                    ]} />
                  </Section>

                  <Section title="Rights">
                    <Grid rows={[
                      ["Ⓒ line", nn(rel.c_line)],
                      ["℗ line", nn(rel.p_line)],
                      ["Label", nn(rel.label)],
                      ["Parent label", nn(rel.parent_label)],
                      ["Copyright year", nn(rel.copyright_year)],
                      ["Copyright holder", nn(rel.copyright_holder)],
                      ["Producer", nn(rel.producer)],
                      ["Publisher", nn(rel.publisher)],
                      ["ISRC (release-level)", nn(rel.isrc)],
                    ]} />
                  </Section>

                  <Section title="Contributors" hideIfEmpty>
                    <ContributorList label="Performing contributors" items={rel.performing_contributors} />
                    <ContributorList label="Non-performing contributors" items={rel.non_performing_contributors} />
                  </Section>

                  <Section title="Restrictions" hideIfEmpty>
                    <RestrictionsBlock rel={rel} />
                  </Section>

                  <Section title="Preview clips / socials" hideIfEmpty>
                    <ClipsBlock clips={rel.clips} spotify={rel.spotify_link} apple={rel.apple_music_link} />
                  </Section>

                  <Section title="Created by">
                    <Grid rows={[
                      ["Name", nn(rel.created_by_name)],
                      ["Email", nn(rel.created_by_email)],
                      ["Role", nn(rel.created_by_role)],
                    ]} />
                  </Section>

                  {(rel.liner_notes || "").trim() && (
                    <Section title="Liner notes">
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{rel.liner_notes}</p>
                    </Section>
                  )}
                </div>
              </div>

              {/* Tracks / Video */}
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500 mb-3">
                  {isVideo ? "Video file" : `Tracks (${(rel.tracks || []).length})`}
                </div>
                <div className="space-y-3">
                  {(rel.tracks || []).length === 0 && !rel.audio_url && (
                    <div className="rounded-xl border border-dashed border-white/10 py-8 text-center text-sm text-slate-500">
                      No {isVideo ? "video" : "audio"} file was uploaded with this release.
                    </div>
                  )}
                  {(rel.tracks || []).length === 0 && rel.audio_url && (
                    <TrackRow index={1} track={{ title: rel.title, audio_url: rel.audio_url }} isVideo={isVideo} />
                  )}
                  {(rel.tracks || []).map((t, i) => (
                    <TrackRow key={t.track_id || i} index={i + 1} track={t} isVideo={isVideo} />
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children, hideIfEmpty = false }) {
  if (hideIfEmpty) {
    // Only render when at least one non-empty descendant is present
    const arr = Array.isArray(children) ? children : [children];
    const hasContent = arr.some((c) => c && !(c.props && c.props.__empty));
    if (!hasContent) return null;
  }
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500 mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Grid({ rows }) {
  const clean = rows.filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (clean.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      {clean.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[10px] tracking-[0.15em] uppercase font-medium text-slate-500">{k}</dt>
          <dd className="mt-0.5 text-sm text-white truncate" title={String(v)}>{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function ContributorList({ label, items }) {
  const list = Array.isArray(items) ? items.filter((x) => x && (x.name || x.role)) : [];
  if (list.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] tracking-[0.15em] uppercase font-medium text-slate-500 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-2">
        {list.map((c, i) => (
          <div key={i} className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-slate-200">
            <span className="text-white">{c.name || "—"}</span>
            {c.role && <span className="text-slate-400"> · {c.role}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function RestrictionsBlock({ rel }) {
  const r = rel.restrictions || {};
  const terr = Array.isArray(r.territories_denied) ? r.territories_denied : [];
  const svc = Array.isArray(r.services_denied) ? r.services_denied : [];
  if (terr.length === 0 && svc.length === 0) return null;
  return (
    <div className="space-y-3">
      {terr.length > 0 && (
        <div>
          <div className="text-[10px] tracking-[0.15em] uppercase font-medium text-slate-500 mb-1.5">Territories excluded ({terr.length})</div>
          <div className="text-sm text-slate-200">{terr.slice(0, 20).join(", ")}{terr.length > 20 ? ` +${terr.length - 20} more` : ""}</div>
        </div>
      )}
      {svc.length > 0 && (
        <div>
          <div className="text-[10px] tracking-[0.15em] uppercase font-medium text-slate-500 mb-1.5">Services excluded ({svc.length})</div>
          <div className="text-sm text-slate-200">{svc.slice(0, 20).join(", ")}{svc.length > 20 ? ` +${svc.length - 20} more` : ""}</div>
        </div>
      )}
    </div>
  );
}

function ClipsBlock({ clips, spotify, apple }) {
  const c = clips || {};
  const rows = [
    ["Preview start", c.preview_start],
    ["YouTube channel", c.youtube?.channel_id || c.youtube?.channel],
    ["Facebook page", c.facebook?.page_id || c.facebook?.url],
    ["TikTok handle", c.tiktok?.handle || c.tiktok?.url],
    ["Spotify link", spotify],
    ["Apple Music link", apple],
  ].filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (rows.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      {rows.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[10px] tracking-[0.15em] uppercase font-medium text-slate-500">{k}</dt>
          <dd className="mt-0.5 text-sm text-white truncate" title={String(v)}>
            {String(v).startsWith("http") ? (
              <a href={v} target="_blank" rel="noreferrer" className="hover:underline">{v}</a>
            ) : String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function TrackRow({ index, track, isVideo }) {
  const url = track.audio_url ? mediaUrl(track.audio_url) : "";
  return (
    <div
      data-testid={`track-row-${index}`}
      className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-sm text-slate-400 shrink-0 tabular-nums">
          {String(index).padStart(2, "0")}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white truncate">{track.title || `Track ${index}`}</div>
          <div className="text-[11px] text-slate-500 truncate">
            {[track.display_artist, track.isrc && `ISRC ${track.isrc}`, track.length].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
      </div>
      <div className="sm:min-w-[300px] flex items-center gap-3">
        {url ? (
          isVideo ? (
            <video controls src={url} className="w-full rounded-lg max-h-[220px] bg-black" preload="metadata" data-testid={`track-video-${index}`} />
          ) : (
            <audio controls src={url} className="w-full" preload="none" data-testid={`track-audio-${index}`} />
          )
        ) : (
          <span className="text-xs text-slate-500">No {isVideo ? "video" : "audio"} attached</span>
        )}
        {url && (
          <a
            href={url}
            download
            target="_blank" rel="noreferrer"
            data-testid={`track-download-${index}`}
            aria-label="Download file"
            className="p-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 shrink-0"
          >
            <Download size={14} />
          </a>
        )}
      </div>
    </div>
  );
}
